import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Token } from 'src/app/models/server/token';

import { Rights } from 'src/app/models/server/enums/rights';
import { Login } from '../../models/login';
import { Authenticate } from '../../models/server/authenticate';
import { IAuthenticate } from '../../models/server/interface/authenticate.interface';
import { BoxesStateService } from '../state/boxes-state.service';
import { ConnectedUsersStateService } from '../state/connected-users-state.service';
import { TChatStateService } from '../state/tchat-state.service';
import { ViewsStateService } from '../state/views-state.service';
import { WebSocketNotificationService } from '../ws/websocket-notification.service';
import { WebSocketTchatService } from '../ws/websocket-tchat.service';
import { ParamsService } from './params.service';
import { ServiceBase } from './service-base';

@Injectable({
    providedIn: 'root',
})
export class AuthService extends ServiceBase {
    /**
     * Constructeur
     */
    constructor(
        private http: HttpClient,
        public override connectedUserStateService: ConnectedUsersStateService,
        private webSocketNotificationService: WebSocketNotificationService,
        private webSocketTchatService: WebSocketTchatService,
        private tchatService: TChatStateService,
        private boxesStateService: BoxesStateService,
        private paramsService: ParamsService,
        private viewsStateService: ViewsStateService
    ) {
        super(connectedUserStateService);
    }

    /**
     * Authentification de l'utilisateur
     * @param login: Information pour l'authantification
     * */
    public logon(login: Login): Observable<Authenticate> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.post<IAuthenticate>(this.baseUrl + `/api/auth/logon`, login, httpOptions).pipe(
            map((x) => {
                let connectedUser = Authenticate.from(x);

                if (connectedUser.rights && connectedUser.rights.length > 0 && connectedUser.hasSomeRight(Rights.BoxView, Rights.WorkflowView)) {
                    this.connectedUserStateService.setUserConnected(connectedUser);
                    return connectedUser;
                } else {
                    return null;
                }
            })
        );
    }

    // Deconnexion de l'utilisateur
    public logoff(token: string): Observable<any> {
        // Appel de la Web Api
        return this.http.delete(this.baseUrl + `/api/auth/logoff`).pipe(
            map((x) => {
                this.webSocketNotificationService.stopSocket(this.connectedUserStateService.getUserConnected().login);
                this.webSocketTchatService.stopSocket(this.connectedUserStateService.getUserConnected().login);
                this.tchatService.roomSelectedChange(null);
                this.connectedUserStateService.setUserConnected(null);
                this.boxesStateService.setBoxesGroupsList([]);
                this.viewsStateService.setSelectedView(null);
                return x;
            })
        );
    }

    // Deconnexion sauvage de l'utilisateur
    public wildLogoff(token: string): boolean {
        let data = { token: token };

        // Supprime les données en session et storage local
        this.connectedUserStateService.setUserConnected(null);
        this.boxesStateService.setBoxesGroupsList([]);
        this.viewsStateService.setSelectedView(null);

        // utilisé car le http n'est plus utilisable une fois la page html a commencé à être déchargée (beforeunload)
        return navigator.sendBeacon(this.baseUrl + '/api/auth/logoff', JSON.stringify(data));
    }

    /**
     * Vérifit la validité du token.
     */
    public checkToken(): Observable<Token> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };
        return this.http.get<Token>(this.baseUrl + `/api/auth/token`, httpOptions).pipe(
            map((x) => {
                let token = new Token();
                token.accessToken = x.accessToken;
                token.expireOn = new Date(x.expireOn);
                return token;
            })
        );
    }
    /**
     * Garde le token en vie.
     */
    public keepAliveToken(): Observable<boolean> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };
        return this.http.patch<Token>(this.baseUrl + `/api/auth/token`, httpOptions).pipe(
            map((x) => {
                this.connectedUserStateService.updateUserToken(x);
                return true;
            })
        );
    }

    /**
     * Recupération de l'utilisateur par rapport au token
     */
    public getCurrentUser(token: string): Observable<IAuthenticate> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.get<IAuthenticate>(this.baseUrl + `/api/auth/token/${token}`, httpOptions).pipe(
            map((x) => {
                let connectedUser = Authenticate.from(x);
                this.connectedUserStateService.setUserConnected(connectedUser);
                return x;
            })
        );
    }
}
